import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { catchError, defer, firstValueFrom, forkJoin, from, Observable, switchMap, throwError, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { TermsAgreementDialog } from '../shared/components/terms-agreement-dialog.component';

export interface TermsConsent { termsAccepted: boolean; termsVersion?: string; }
export class TermsConsentError extends Error {
  readonly error: { detail: string };
  constructor(key: 'TERMS_UNAVAILABLE' | 'TERMS_REQUIRED') { super(key); this.error = { detail: key }; }
}

@Injectable({ providedIn: 'root' })
export class TermsAgreementService {
  private readonly http = inject(HttpClient);
  private readonly dialogs = inject(MatDialog);
  private readonly translate = inject(TranslateService);

  authorize<T>(request: (consent: TermsConsent) => Observable<T>, registrationAccepted?: boolean): Observable<T> {
    if (registrationAccepted !== undefined) {
      return defer(() => from(this.obtain(registrationAccepted))).pipe(switchMap(consent => request(consent)));
    }
    // Credentials identify the account first; only the server can decide whether consent is current.
    return defer(() => request({ termsAccepted: false })).pipe(catchError(error => {
      if (error?.status !== 428 || error?.error?.code !== 'TERMS_REQUIRED') return throwError(() => error);
      const version = error.error.version;
      if (typeof version !== 'string' || !version.trim() || version.length > 64) {
        return throwError(() => new TermsConsentError('TERMS_UNAVAILABLE'));
      }
      return from(this.obtain(false, version)).pipe(switchMap(consent => request(consent)));
    }));
  }
  private async obtain(registrationAccepted: boolean, challengedVersion?: string): Promise<TermsConsent> {
    const lang = this.translate.getCurrentLang() === 'en' ? 'en' : 'ar';
    const policy = challengedVersion ? { version: challengedVersion } : await firstValueFrom(this.http.get<{ version: string }>(environment.apiUrl + '/api/Auth/terms').pipe(
      timeout(15000), catchError(() => throwError(() => new TermsConsentError('TERMS_UNAVAILABLE')))
    ));
    if (typeof policy?.version !== 'string' || !policy.version.trim() || policy.version.length > 64) {
      throw new TermsConsentError('TERMS_UNAVAILABLE');
    }
    if (!registrationAccepted) {
      const documents = await firstValueFrom(forkJoin({
        terms: this.http.get(`html-pages/terms-conditions.${lang}.html`, { responseType: 'text' }),
        privacy: this.http.get(`html-pages/data-protection.${lang}.html`, { responseType: 'text' }),
      }).pipe(timeout(15000), catchError(() => throwError(() => new TermsConsentError('TERMS_UNAVAILABLE')))));
      const accepted = await firstValueFrom(this.dialogs.open(TermsAgreementDialog, {
        width: '760px', maxWidth: '95vw', maxHeight: '90vh', autoFocus: false,
        data: { ...documents, version: policy.version },
      }).afterClosed());
      if (accepted !== true) throw new TermsConsentError('TERMS_REQUIRED');
    }
    return { termsAccepted: true, termsVersion: policy.version };
  }
}
